<script lang="ts">
    import { CommandButton } from "ceyper-common";

    import { COMMANDS } from "$lib/commands";
    import { onMount } from "svelte";
    import { file, interaction, frameIndex, frames } from "$lib/state";
    import { animationPlayback } from "$lib/animation/playback";
    import Preview from "$lib/image/Preview.svelte";
    import IconCommandButton from "$lib/utility/IconCommandButton.svelte";
    import Player from "$lib/image/Player.svelte";


    function changeFrame(target: number) {
        COMMANDS.invoke("animation.change-frame", target);
    }

    let framesElement: HTMLElement;
    const frameElements = [] as HTMLElement[];
    $: frameElements[$frameIndex!]?.scrollIntoView({ behavior: "smooth", block: "end" });

    let container: HTMLElement;
    onMount(() => COMMANDS.actions.mouseScope(container, "animation").destroy);

    let scrollTarget: number | null = null;
    function onScroll(event: WheelEvent) {
        if (event.deltaX) return;

        event.preventDefault();
        event.stopPropagation();

        const current = scrollTarget ?? framesElement.scrollLeft;
        scrollTarget = current + Math.clamp(event.deltaY, -1, 1) * 16 * 9;

        framesElement.scrollTo({
            left: scrollTarget,
            behavior: "smooth"
        });
        setTimeout(() => scrollTarget = null, 250);
    }

    let frame = 0;
    $: frame = $animationPlayback.previewFrame;

    // let duration = updateDuration(frame);
    $: duration = $frames?.[frame]?.duration;
    function changeDuration(e: Event) {
        const value = +(e.target as HTMLInputElement).value;
        if (!$frames || !value || value <= 0) return;
        COMMANDS.invoke("animation.set-frame-duration", frame, value);
    }

    function applyDurationToAll() {
        const value = $frames?.[frame]?.duration;
        if (!$frames || !value || value <= 0) return;
        COMMANDS.invoke("animation.set-all-frame-durations", value);
    }

    function seekPreview(e: Event) {
        COMMANDS.invoke("animation.load", +(e.target as HTMLInputElement).value);
    }

    function scrollRange(e: WheelEvent) {
        if (e.deltaY == 0) return;
        const count = $frames!.length
        let target: number;
        if (e.deltaY > 0) {
            target = (frame + 1) % count;
        } else {
            target = (frame - 1 + count) % count;
        }
        COMMANDS.invoke("animation.load", target);
        e.preventDefault();
        e.stopPropagation();
    }
</script>

<!----------------------------------------------------------------------------->

<div></div>
<div class="animation" bind:this={container} style="--aspect: {$file?.width ?? 1} / {$file?.height ?? 1}">
    <div class="controls">

        <IconCommandButton command={COMMANDS.commands["animation.first-frame"]} icon="arrow-left-end-16" tooltip="Select first frame"/>
        <IconCommandButton command={COMMANDS.commands["animation.prev-frame"]} icon="arrow-left-16" tooltip="Select previous frame"/>
        <IconCommandButton command={COMMANDS.commands["animation.next-frame"]} icon="arrow-right-16" tooltip="Select next frame"/>
        <IconCommandButton command={COMMANDS.commands["animation.last-frame"]} icon="arrow-right-end-16" tooltip="Select last frame" />

        <CommandButton command={COMMANDS.commands["animation.add-frame"]} label="添加" tooltip="Add frame"/>
        <CommandButton command={COMMANDS.commands["animation.duplicate-frame"]} label="副本" tooltip="Duplicate frame"/>
        <CommandButton command={COMMANDS.commands["animation.remove-frame"]} label="删除" tooltip="Remove frame"/>
<!--        |-->

<!--        <input class="step" type="number" bind:value={$EditorSettings["shadow-before-steps"].value} on:blur={EditorSettings.save} {...input.number($EditorSettings["shadow-before-steps"])}/>-->
<!--        <input class="step" type="number" bind:value={$EditorSettings["shadow-after-steps"].value} on:blur={EditorSettings.save} {...input.number($EditorSettings["shadow-after-steps"])} />-->
    </div>

    {#if $file && $interaction && $frames}
    {#key $file}
        <div bind:this={framesElement} class="frames" class:show-index={true} on:wheel={onScroll}>
            {#each $frames as _, i}
                <div class="scroll-stop start"></div>
                <button bind:this={frameElements[i]} class="frame" class:active={i === $frameIndex} on:click={() => changeFrame(i)}>
                    <Preview file={$file} frame={i} />
                    <span class="index">{i}</span>
                </button>
                <div class="scroll-stop end"></div>
            {/each}
        </div>

<!--        <div class="properties">-->
<!--            &lt;!&ndash;{#key $frameIndex}&ndash;&gt;-->
<!--            &lt;!&ndash;    <label>&ndash;&gt;-->
<!--            &lt;!&ndash;        <span>Duration</span>&ndash;&gt;-->
<!--            &lt;!&ndash;        <input type="number" bind:value={$interaction.frame.delay} step="1" min="1"/>&ndash;&gt;-->
<!--            &lt;!&ndash;    </label>&ndash;&gt;-->
<!--            &lt;!&ndash;{/key}&ndash;&gt;-->
<!--        </div>-->

        <div class="controls right">
            {#if $animationPlayback.playing}
                <IconCommandButton command={COMMANDS.commands["animation.pause"]} icon="animation-pause-16" tooltip="Pause" />
            {:else}
                <IconCommandButton command={COMMANDS.commands["animation.play"]} icon="animation-play-16" tooltip="Play" />
            {/if}
            <IconCommandButton command={COMMANDS.commands["animation.toggle-loop"]} icon="animation-loop-16" tooltip="Loop" color={$animationPlayback.loop ? "active" : undefined} />
        </div>
        <div class="player" style="--frames: {$frames?.length ?? 1};" on:wheel={scrollRange}>
            <Player file={$file} frame={$animationPlayback.previewFrame} />
            <div class="frame-indicator">
                <input type="range" value={$animationPlayback.previewFrame} min="0" max={$frames.length - 1} step="1" on:input={seekPreview} />
                {#if $frames.length > 1}
                    {#each $frames as _, i}
                        <div class:active={i === $animationPlayback.previewFrame}></div>
                    {/each}
                {/if}
            </div>
        </div>

        <div class="properties">
            <input type="number" value={duration} step="1" min="1" on:input={changeDuration} title="Duration" />
            <IconCommandButton on:click={applyDurationToAll} icon="arrow-right-end-16" tooltip="Apply duration to all frames" />
        </div>
    {/key}
    {/if}
</div>

<!----------------------------------------------------------------------------->

<style>
    .player {
        position: relative;
        grid-area: player;
        flex: none;
        display: flex;
        flex-direction: column;
        width: 8em;
        padding: 1em 0.5em;
        border-left: 1px solid var(--gap-color);

        & :global(canvas) {
            width: 100%;
            aspect-ratio: var(--aspect);
        }

        & .frame-indicator {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
            width: 100%;

            & input {
                all: unset;
                position: absolute;
                inset: 0;

                &::-moz-range-thumb {
                    width: calc(100% / var(--frames));
                    height: 100%;
                    background: transparent;
                }
                &::-webkit-slider-thumb {
                    width: calc(100% / var(--frames));
                    height: 100%;
                    background: transparent;
                }
            }

            & div {
                all: unset;
                width: 100%;
                height: 16px;
                position: relative;
                pointer-events: none;

                &::before {
                    content: "";
                    position: absolute;
                    inset: 4px 2px 6px;

                    background: var(--t400);
                }

                &.active::before {
                    background: var(--t100);
                    inset: 4px 2px 4px;
                }
            }
        }
    }
    .animation {
        display: grid;
        grid-template:
            "controls-left controls-right" max-content
            "frames        player" 1fr
            "frames        properties" max-content /
            1fr max-content;
        min-height: 0;
        height: 100%;
    }

    .controls {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 2px 0.5em;

        &.right {
            border-left: 1px solid var(--gap-color);
            place-content: flex-end;
        }

        & :global(button:not(.icon-button)) {
            border: none;
            font-size: 0.8rem;
            padding: 0 0.25em;
            height: 1.5em;
            text-align: center;
        }
    }

    .frames {
        grid-area: frames;

        display: flex;
        flex-flow: row nowrap;
        padding-top: 1em;

        overflow-x: scroll;
        scrollbar-width: thin;
        scroll-snap-type: x proximity;
    }

    .scroll-stop {
        width: 0.5em;
        flex: none;

        &.start {
            scroll-snap-align: start;
        }

        &.end {
            scroll-snap-align: end;
        }
    }

    .frame {
        all: unset;
        flex: none;
        display: flex;
        width: 8em;
        height: max-content;
        user-select: none;
        position: relative;

        &.active {
            outline: 2px solid white;
        }

        & :global(canvas) {
            width: 100%;
            object-fit: contain;
            aspect-ratio: var(--aspect);
        }

        & .index {
            position: absolute;
            top: -4px;
            right: -4px;
            font-family: var(--font-monospace);
            padding-inline: 0.5ch;
            background: var(--g100);
            outline: solid 1px var(--g50);
            border-bottom: solid 1px var(--g50);
            visibility: collapse;
            pointer-events: none;
        }
    }

    .show-index .index {
        visibility: visible;
    }

    .properties {
        grid-area: properties;
        border-left: 1px solid var(--gap-color);
        padding: 0.5em;
        display: flex;
        gap: 4px;

        & input {
            width: 1em;
            flex-grow: 1;
        }
    }
</style>
